import React, { useState } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';

interface Option {
  title?: string;
  label?: string;
  value?: string;
  type?: string;
  description?: string;
  postbackText?: string;
  action?: {
    output_context: string;
    flow_id: string;
  };
}

interface Item {
  title: string;
  subtitle?: string;
  options: Option[];
}

interface MessageContent {
  type?: string;
  text?: string;
  body?: { type: string; text: string };
  items?: Item[]; // legacy
  list?: Item[];  // newer format
}

interface Message {
  content?: MessageContent;
}

interface MessageProps {
  message: Message;
  onListItemClick?: (option: Option) => void;
}

const formatText = (text: any): string => {
  if (typeof text !== 'string') return '';
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*(.*?)\*/g, '<b>$1</b>')        
    .replace(/_(.*?)_/g, '<i>$1</i>')        
    .replace(/~(.*?)~/g, '<s>$1</s>')       
    .replace(/```(.*?)```/g, '<code>$1</code>') 
    .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>') // link
    .replace(/\n/g, '<br />');                 
};

const MessageList: React.FC<MessageProps> = ({ message, onListItemClick }) => {
  const [showOptions, setShowOptions] = useState(false);

  const content = message?.content || {};
  const bodyText = content?.body?.text || content?.text || '';
  const items = content?.list || content?.items || [];
  const listTitle = items?.[0]?.title || 'Options';

  // Collect all options
  const listOptions = items.flatMap((item) => item.options || []);

  return (
    <Box>
      {/* Body text */}
      {bodyText && (
        <Typography variant="body2" sx={{ mb: listOptions.length > 0 ? 2 : 0, whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: formatText(bodyText) }}
>
          {/* {bodyText} */}
        </Typography>
      )}

      {/* Show toggle button only if options exist */}
      {listOptions.length > 0 && (
        <>
          <Button
            variant="outlined"
            onClick={() => setShowOptions((prev) => !prev)}
            sx={{
              mb: 2,
              borderRadius: '20px',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            📋 {showOptions ? 'Hide List' : `View ${listTitle}`}
          </Button>

          {/* List Options */}
          {showOptions && (
            <Stack spacing={1}>
              {listOptions.map((option, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  onClick={() => onListItemClick?.(option)}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '14px',
                  }}
                >
                  {option.title || option.label || 'Untitled Option'}
                </Button>
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default MessageList;
